import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PhantomWallet } from '../services/phantom';
import { useTranslation } from 'react-i18next';

// Типы для Phantom кошелька
interface PhantomWindow extends Window {
  solana?: {
    isPhantom?: boolean;
    connect: () => Promise<{ publicKey: { toString: () => string } }>;
    disconnect: () => Promise<void>;
    on: (event: string, callback: (args: any) => void) => void;
    signMessage: (message: Uint8Array, encoding: string) => Promise<{ signature: Uint8Array }>;
  };
}

// Интерфейс контекста кошелька
interface WalletContextType {
  walletAddress: string | null;
  isConnected: boolean;
  isPhantomInstalled: boolean;
  connectWallet: () => Promise<string | null>;
  disconnectWallet: () => Promise<boolean>;
  signMessage: (message: string) => Promise<Uint8Array | null>;
}

// Создаем контекст
const WalletContext = createContext<WalletContextType | undefined>(undefined);

// Хук для использования контекста
export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

// Интерфейс для пропсов провайдера
interface WalletProviderProps {
  children: ReactNode;
}

// Провайдер контекста
export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [isPhantomInstalled, setIsPhantomInstalled] = useState<boolean>(false);

  // Проверяем наличие Phantom и сохраненного адреса при загрузке
  useEffect(() => {
    const checkPhantom = () => {
      const isInstalled = PhantomWallet.isPhantomInstalled();
      setIsPhantomInstalled(isInstalled);
      
      if (isInstalled) {
        const savedAddress = PhantomWallet.getCurrentWallet();
        if (savedAddress) {
          setWalletAddress(savedAddress);
        }
      }
    };
    
    checkPhantom();
    
    // Слушаем события отключения кошелька
    const phantomWindow = window as PhantomWindow;
    if (phantomWindow.solana) {
      phantomWindow.solana.on('disconnect', () => {
        setWalletAddress(null);
        localStorage.removeItem('walletAddress');
      });
    }
  }, []);

  // Подключение к кошельку
  const connectWallet = async (): Promise<string | null> => {
    try {
      if (!isPhantomInstalled) {
        alert(t('wallet.phantomNotInstalled'));
        window.open('https://phantom.app/', '_blank');
        return null;
      }
      
      const address = await PhantomWallet.connect();
      if (address) {
        setWalletAddress(address);
      }
      return address;
    } catch (error) {
      console.error('Ошибка при подключении кошелька:', error);
      return null;
    }
  };

  // Отключение от кошелька
  const disconnectWallet = async (): Promise<boolean> => {
    try {
      const success = await PhantomWallet.disconnect();
      if (success) {
        setWalletAddress(null);
      }
      return success;
    } catch (error) {
      console.error('Ошибка при отключении кошелька:', error);
      return false;
    }
  };

  // Подписание сообщения
  const signMessage = async (message: string): Promise<Uint8Array | null> => {
    return await PhantomWallet.signMessage(message);
  };

  // Значение контекста
  const value = {
    walletAddress,
    isConnected: !!walletAddress,
    isPhantomInstalled,
    connectWallet,
    disconnectWallet,
    signMessage
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}; 